/**
 * Lo scarto di lavorazione, dal gesto all'archivio (regole 20-bis e 20-quater).
 *
 * La 20-bis dice cosa succede al capo rovinato: cala il conto dei buoni e la
 * causale è obbligatoria. Non diceva cosa succede **alla riga**, e per mesi la
 * risposta è stata «resta dentro `FlowState`»: un array in coda a un documento
 * JSON riletto e riscritto sotto lock a ogni gesto del flusso. Cresceva per
 * sempre, non si poteva interrogare, e — la cosa che conta di più — **non
 * portava la data**: senza, «gli scarti di questa settimana» è una domanda che
 * non si può fare.
 *
 * Da qui la 20-quater: **lo scarto lascia una riga, e la riga resta aperta
 * finché l'ordine non è reintegrato.** È la stessa forma della 8-bis per i
 * fallati in ricezione, e il parallelo finisce qui: quella misura il fornitore
 * e fa partire una mail, questa misura noi e non fa partire niente. Il capo
 * perso non si reclama a nessuno, si rifà.
 *
 * ## Perché la riga porta la scheda dell'ordine
 *
 * Cassetta, ordine, cliente, articolo, sku, colore e taglia si **copiano** allo
 * scarto invece di andarli a rileggere. La cassetta si svuota dentro un collo e
 * quel legame sparisce: uno scarto che rimandasse alla cassetta diventerebbe
 * illeggibile il giorno della spedizione, cioè esattamente quando serve
 * guardarlo. È la stessa scelta di `DefectRecord`.
 *
 * ## Perché il reintegro non si disfa
 *
 * `markRestored` non ha il gemello che libera. Qualcuno ha rifatto quel capo:
 * è un fatto accaduto, e cancellarlo vorrebbe dire riscrivere chi ha fatto
 * cosa. Se il capo si rovina di nuovo, quello è un fatto nuovo e vuole una riga
 * nuova.
 */
import { accept, refuse } from '../common/outcome.js';
/**
 * Le causali dello scarto, e perché sono queste cinque.
 *
 * Una macchina che si inceppa si aggiusta, un filato che si rompe si cambia,
 * un'impostazione sbagliata si corregge a monte: sono tre decisioni diverse, e
 * un conteggio che non le distingue non porta a nessuna di loro. «altro»
 * obbliga a scrivere, perché una voce generica in un elenco incompleto diventa
 * in poche settimane la più usata.
 */
export const SCRAP_REASONS = [
    { k: 'macchina', nome: 'Macchina', nota: 'inceppamento, ago rotto, testa fuori registro' },
    {
        k: 'filato',
        nome: 'Filato o inchiostro',
        nota: 'rottura del filo, colore sbagliato, film difettoso',
    },
    {
        k: 'impostazione',
        nome: 'Impostazione',
        nota: 'file sbagliato, telaio storto, posizione errata',
    },
    { k: 'capo', nome: 'Difetto del capo', nota: 'venuto fuori solo lavorandolo' },
    { k: 'altro', nome: 'Altro', nota: "scrivi in due parole cos'è successo", libera: true },
];
export function isScrapReason(v) {
    return SCRAP_REASONS.some((c) => c.k === v);
}
/** La riga d'archivio che nasce da uno scarto accettato. Nasce aperta. */
export function recordScrap(id, cassetta, reparto, taglia, capi, causale, spiegazione, chi, quando) {
    return {
        id,
        quando,
        cassetta: cassetta.codice,
        reparto,
        ordine: cassetta.ordine,
        cliente: cassetta.cliente,
        articolo: cassetta.articolo,
        sku: cassetta.sku,
        colore: cassetta.colore,
        taglia,
        capi,
        causale,
        spiegazione: spiegazione?.trim() || null,
        chi,
        reintegratoDa: null,
        reintegratoIl: null,
    };
}
/** Il capo perso è stato rifatto: da qui l'avviso si spegne. */
export function markRestored(s, chi, quando) {
    if (s.reintegratoDa !== null) {
        return refuse('20-quater', 'Già reintegrato', `Questo scarto l'ha già reintegrato ${s.reintegratoDa}.`);
    }
    // Un reintegro anonimo dice che l'avviso è sparito e non chi ha rifatto il
    // capo, che è l'unica cosa che serviva sapere.
    if (chi.trim() === '') {
        return refuse('20-quater', 'Nessun nome', 'Un reintegro senza un nome non vale.');
    }
    return accept({ ...s, reintegratoDa: chi, reintegratoIl: quando }, 'Reintegrato', `${s.capi} capo/i di ${s.articolo} ${s.colore} taglia ${s.taglia} rifatti da ${chi}: ` +
        `l'ordine ${s.ordine} torna completo.`);
}
/**
 * Gli scarti ancora da reintegrare, il più recente in cima.
 *
 * È la sorgente dell'avviso, e l'ordine è quello che serve a chi lo legge: il
 * più vecchio in cima manderebbe la novità in fondo a un elenco che si guarda
 * di sfuggita.
 */
export function openScraps(tutti) {
    return tutti
        .filter((s) => s.reintegratoDa === null)
        .toSorted((a, b) => b.quando.getTime() - a.quando.getTime());
}
/**
 * Gli scarti raggruppati per causale: è la vista che porta a una decisione.
 *
 * Prende un elenco e non `FlowState`, ed è la differenza che questo file
 * introduce: chi chiama decide **quali** scarti guardare — quelli di oggi,
 * quelli aperti, tutti — invece di riceverne per forza l'intera storia.
 */
export function scrapsByReason(scarti) {
    const per = new Map();
    for (const sc of scarti) {
        const v = per.get(sc.causale) ?? { capi: 0, spiegazioni: [] };
        v.capi += sc.capi;
        if (sc.spiegazione)
            v.spiegazioni.push(sc.spiegazione);
        per.set(sc.causale, v);
    }
    const totale = [...per.values()].reduce((n, v) => n + v.capi, 0);
    return [...per.entries()]
        .map(([causale, v]) => ({
        causale,
        capi: v.capi,
        percentuale: totale > 0 ? Math.round((v.capi / totale) * 100) : 0,
        // i testi scritti a mano si rileggono: un motivo che torna spesso merita
        // di diventare una voce sua e smettere di finire in «altro»
        spiegazioni: v.spiegazioni,
    }))
        .sort((a, b) => b.capi - a.capi);
}
//# sourceMappingURL=scraps.js.map